import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/design01/Navigation';
import {
    Award,
    Globe,
    Leaf,
    Shield,
    Star,
    MapPin,
    Mail,
    Phone,
    Users,
    Truck,
    CheckCircle
} from 'lucide-react';

const HomePage = () => {
    return (
        <>
            {/* Head tags (React 19 hoists these to <head>) */}
            <title>Claude&apos;s Special - Premium Ceylon Cinnamon Exports | Sri Lankan Spices</title>
            <meta
                name="description"
                content="Claude's Special exports the finest Ceylon Cinnamon from Sri Lanka to Australia. Discover authentic, premium quality cinnamon with rich heritage and unmatched flavor."
            />
            <meta
                name="keywords"
                content="Ceylon Cinnamon, Sri Lankan spices, cinnamon export, authentic cinnamon, premium spices, Australia import"
            />
            <meta property="og:title" content="Claude's Special - Premium Ceylon Cinnamon Exports" />
            <meta
                property="og:description"
                content="Exporting the finest Ceylon Cinnamon from Sri Lanka to Australia with authentic quality and rich heritage."
            />
            <meta property="og:type" content="website" />
            <link rel="canonical" href="/" />
            {/* If you have a share image, you can add:
      <meta property="og:image" content="https://yourcdn.com/og-image.jpg" />
      <meta name="twitter:image" content="https://yourcdn.com/og-image.jpg" />
      */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Claude's Special - Premium Ceylon Cinnamon Exports" />
            <meta
                name="twitter:description"
                content="Exporting the finest Ceylon Cinnamon from Sri Lanka to Australia with authentic quality and rich heritage."
            />

            <Navigation />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.pexels.com/photos/4198933/pexels-photo-4198933.jpeg"
                        alt="Ceylon Cinnamon plantation in Sri Lanka"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-900/80 via-orange-800/70 to-amber-700/60"></div>
                </div>

                <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
                    <div className="mb-6">
                        <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8">
                            <Leaf className="h-6 w-6 text-amber-300" />
                            <span className="text-lg font-medium">Authentic Ceylon Heritage</span>
                        </div>
                    </div>

                    <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                        Claude&apos;s Special
                    </h1>
                    <p className="text-xl md:text-2xl mb-8 opacity-90 leading-relaxed">
                        Exporting the Finest Ceylon Cinnamon from Sri Lanka to Australia
                    </p>
                    <p className="text-lg mb-10 opacity-80 max-w-2xl mx-auto">
                        Experience the authentic taste of true Ceylon Cinnamon, sourced directly from the lush highlands of Sri Lanka where this precious spice has been cultivated for centuries.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link
                            to="/ceylon-cinnamon"
                            className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                        >
                            Discover Ceylon Cinnamon
                        </Link>
                        <a
                            href="#contact"
                            className="bg-white/20 hover:bg-white/30 text-white border-2 border-white/30 hover:border-white/50 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 backdrop-blur-sm"
                        >
                            Contact Us
                        </a>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                Our Heritage of Excellence
                            </h2>
                            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                                Claude&apos;s Special is built on generations of expertise in cultivating and processing the world&apos;s finest Ceylon Cinnamon. Rooted in the rich soil and traditions of Sri Lanka, we bring you the authentic taste that has made Ceylon Cinnamon the gold standard worldwide.
                            </p>
                            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                                Our commitment extends beyond quality - we work directly with local farmers, ensuring sustainable practices and fair trade that supports Sri Lankan communities while delivering unparalleled cinnamon to Australia.
                            </p>

                            <div className="grid sm:grid-cols-2 gap-6">
                                <div className="flex items-start space-x-3">
                                    <Award className="h-6 w-6 text-amber-600 mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Premium Quality</h3>
                                        <p className="text-gray-600 text-sm">Certified authentic Ceylon Cinnamon</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <Shield className="h-6 w-6 text-amber-600 mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Quality Assured</h3>
                                        <p className="text-gray-600 text-sm">Rigorous testing and certification</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <Users className="h-6 w-6 text-amber-600 mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Fair Trade</h3>
                                        <p className="text-gray-600 text-sm">Supporting local communities</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <Truck className="h-6 w-6 text-amber-600 mt-1 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-semibold text-gray-900">Reliable Export</h3>
                                        <p className="text-gray-600 text-sm">Timely delivery to Australia</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                                <img
                                    src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg"
                                    alt="Traditional cinnamon processing in Sri Lanka"
                                    className="w-full h-96 object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-6 shadow-xl">
                                <div className="text-white text-center">
                                    <div className="text-3xl font-bold">25+</div>
                                    <div className="text-sm opacity-90">Years Experience</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold text-gray-900 mb-4">
                            Why Choose Claude&apos;s Special?
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            We bridge the authentic flavors of Sri Lanka with the quality standards Australia demands
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                <Globe className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Global Standards</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Our processing facilities meet international export standards, ensuring your Ceylon Cinnamon arrives fresh and authentic in Australia.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                <Star className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Premium Selection</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Hand-selected from the best cinnamon gardens in Sri Lanka&apos;s central highlands, where the climate creates the most aromatic and flavorful cinnamon.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 group">
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                <CheckCircle className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4">Trusted Partner</h3>
                            <p className="text-gray-600 leading-relaxed">
                                Established relationships with Australian importers and retailers, ensuring smooth customs clearance and reliable delivery schedules.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Export Information */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="relative">
                            <img
                                src="https://images.pexels.com/photos/4198951/pexels-photo-4198951.jpeg"
                                alt="Ceylon Cinnamon sticks ready for export"
                                className="w-full h-96 object-cover rounded-2xl shadow-xl"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-amber-900/50 to-transparent rounded-2xl"></div>
                        </div>

                        <div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                Export to Australia
                            </h2>
                            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                                We specialize in exporting premium Ceylon Cinnamon to Australia, meeting all regulatory requirements and quality standards. Our streamlined export process ensures your orders arrive fresh and on time.
                            </p>

                            <div className="space-y-4 mb-8">
                                <div className="flex items-center space-x-3">
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                    <span className="text-gray-700">HACCP and ISO certified processing</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                    <span className="text-gray-700">Australian quarantine compliance</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                    <span className="text-gray-700">Bulk and retail packaging options</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                    <span className="text-gray-700">Competitive wholesale pricing</span>
                                </div>
                            </div>

                            <Link
                                to="/ceylon-cinnamon"
                                className="inline-flex items-center bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                            >
                                Learn About Our Cinnamon
                                <Leaf className="ml-2 h-5 w-5" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-20 bg-gradient-to-br from-amber-900 to-orange-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4">Get In Touch</h2>
                        <p className="text-xl opacity-90">Ready to import authentic Ceylon Cinnamon to Australia?</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-3">
                                    <MapPin className="h-5 w-5 text-amber-300" />
                                    <span>Colombo, Sri Lanka</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <Phone className="h-5 w-5 text-amber-300" />
                                    <span>+94 11 XXX XXXX</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <Mail className="h-5 w-5 text-amber-300" />
                                    <span>exports@claudesspecial.lk</span>
                                </div>
                            </div>

                            <div className="mt-8">
                                <h4 className="text-lg font-semibold mb-4">Export Specialties</h4>
                                <ul className="space-y-2 opacity-90">
                                    <li>• Premium Ceylon Cinnamon Sticks</li>
                                    <li>• Ground Ceylon Cinnamon Powder</li>
                                    <li>• Cinnamon Leaf Oil</li>
                                    <li>• Custom Packaging Solutions</li>
                                </ul>
                            </div>
                        </div>

                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                            <h3 className="text-2xl font-bold mb-6">Export Inquiry</h3>
                            <form className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Company Name</label>
                                    <input
                                        type="text"
                                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/20 transition-colors duration-200 placeholder-white/60"
                                        placeholder="Your company name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Email Address</label>
                                    <input
                                        type="email"
                                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/20 transition-colors duration-200 placeholder-white/60"
                                        placeholder="your@email.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Quantity Required</label>
                                    <select className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/20 transition-colors duration-200">
                                        <option value="">Select quantity range</option>
                                        <option value="1-100kg">1-100 kg</option>
                                        <option value="100-500kg">100-500 kg</option>
                                        <option value="500kg+">500+ kg</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Message</label>
                                    <textarea
                                        rows={4}
                                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 focus:border-amber-300 focus:ring-2 focus:ring-amber-300/20 transition-colors duration-200 placeholder-white/60 resize-none"
                                        placeholder="Tell us about your requirements..."
                                    ></textarea>
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                                >
                                    Send Export Inquiry
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-3 gap-8">
                        <div>
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg">
                                    <Leaf className="h-5 w-5 text-white" />
                                </div>
                                <span className="text-lg font-bold">Claude&apos;s Special</span>
                            </div>
                            <p className="text-gray-400 leading-relaxed">
                                Bringing the authentic taste of Ceylon Cinnamon from Sri Lanka&apos;s highlands to Australia with quality and tradition.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-lg font-semibold mb-4">Products</h4>
                            <ul className="space-y-2 text-gray-400">
                                <li>Ceylon Cinnamon Sticks</li>
                                <li>Ground Cinnamon Powder</li>
                                <li>Cinnamon Leaf Oil</li>
                                <li>Custom Packaging</li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                            <ul className="space-y-2">
                                <li>
                                    <Link to="/ceylon-cinnamon" className="text-gray-400 hover:text-amber-300 transition-colors duration-200">
                                        About Ceylon Cinnamon
                                    </Link>
                                </li>
                                <li>
                                    <a href="#contact" className="text-gray-400 hover:text-amber-300 transition-colors duration-200">
                                        Export Inquiry
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 pt-8 mt-8 text-center">
                        <p className="text-gray-400">
                            &copy; 2025 Claude&apos;s Special. All rights reserved. | Exporting authentic Ceylon Cinnamon to Australia
                        </p>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default HomePage;
